/**
 * BHCR Contact Form (<bhcr-contact-form>)
 *
 * Validates the native Shopify contact form, routes submissions to the
 * appropriate inbox via hidden fields, and submits via fetch so the submit
 * button can show a short success state without a full page reload.
 */
if (!customElements.get('bhcr-contact-form')) {
  const SUPPORT_REASONS = new Set(['online-order', 'refunds-and-returns', 'complaints']);
  const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const UNSAFE_TEXT_PATTERN = /[<>]/;

  class BhcrContactForm extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form');
      if (!this.form) return;

      this.submitButton = this.form.querySelector('[type="submit"]');
      this.reasonSelect = this.form.querySelector('[data-contact-reason]');
      this.routingInput = this.form.querySelector('[data-contact-routing]');
      this.tagsInput = this.form.querySelector('[data-contact-tags]');
      this.nameInput = this.form.querySelector('[data-contact-name]');
      this.firstNameInput = this.form.querySelector('[data-contact-first-name]');
      this.lastNameInput = this.form.querySelector('[data-contact-last-name]');
      this.messageInput = this.form.querySelector('[data-contact-message]');
      this.emailInput = this.form.querySelector('[data-contact-email]');
      this.honeypot = this.form.querySelector('[data-contact-honeypot]');
      this.statusRegion = this.form.querySelector('[data-contact-status]');
      this.serverSuccess = this.querySelector('[data-contact-server-success]');

      this.supportEmail = this.dataset.supportEmail || 'support@belhorizon.coffee';
      this.generalEmail = this.dataset.generalEmail || 'gregoire@belhorizon.coffee';
      this.sendLabel = this.dataset.sendLabel || 'Send';
      this.successLabel = this.dataset.successLabel || 'Your message has been sent. We will respond shortly.';
      this.errorLabel = this.dataset.errorLabel || 'Something went wrong. Please try again.';
      this.validationRequired = this.dataset.validationRequired || 'This field is required.';
      this.validationEmail = this.dataset.validationEmail || 'Please enter a valid email address.';
      this.validationReason = this.dataset.validationReason || 'Please select a reason for contacting.';
      this.validationInvalidCharacters =
        this.dataset.validationInvalidCharacters || 'This field contains invalid characters.';
      this.successDuration = Number.parseInt(this.dataset.successDuration || '3000', 10);

      this._submitting = false;
      this._boundSubmit = this.onSubmit.bind(this);
      this._boundReasonChange = this.updateRouting.bind(this);

      this.form.addEventListener('submit', this._boundSubmit);
      this.reasonSelect?.addEventListener('change', this._boundReasonChange);
      this.updateRouting();

      if (this.serverSuccess && !this.serverSuccess.hidden) {
        this.hideServerSuccessBanner();
        this.showButtonSuccess();
      }
    }

    disconnectedCallback() {
      this.form?.removeEventListener('submit', this._boundSubmit);
      this.reasonSelect?.removeEventListener('change', this._boundReasonChange);
      if (this._successTimeout) window.clearTimeout(this._successTimeout);
    }

    getSelectedReasonSlug() {
      return this.reasonSelect?.selectedOptions[0]?.dataset.reasonSlug || '';
    }

    updateRouting() {
      const reason = this.getSelectedReasonSlug();
      const isSupport = SUPPORT_REASONS.has(reason);
      const email = isSupport ? this.supportEmail : this.generalEmail;

      if (this.routingInput) this.routingInput.value = email;
      if (this.tagsInput) this.tagsInput.value = isSupport ? 'contact-support' : 'contact-general';
    }

    combineName() {
      const first = this.firstNameInput?.value.trim() || '';
      const last = this.lastNameInput?.value.trim() || '';
      if (this.nameInput) this.nameInput.value = [first, last].filter(Boolean).join(' ');
    }

    setSubmittingState(isSubmitting) {
      if (!this.submitButton) return;
      this.submitButton.disabled = isSubmitting;
      this.submitButton.setAttribute('aria-busy', isSubmitting ? 'true' : 'false');
    }

    clearStatus() {
      if (!this.statusRegion) return;
      this.statusRegion.textContent = '';
      this.statusRegion.hidden = true;
    }

    showStatus(message, isError = false) {
      if (!this.statusRegion) return;
      this.statusRegion.textContent = message;
      this.statusRegion.hidden = false;
      this.statusRegion.classList.toggle('bhcr-contact__status--error', isError);
    }

    hideServerSuccessBanner() {
      if (this.serverSuccess) this.serverSuccess.hidden = true;
    }

    showButtonSuccess() {
      if (!this.submitButton) return;

      const originalLabel = this.sendLabel;
      this.submitButton.textContent = this.successLabel;
      this.submitButton.disabled = true;
      this.submitButton.setAttribute('aria-busy', 'false');

      if (this._successTimeout) window.clearTimeout(this._successTimeout);
      this._successTimeout = window.setTimeout(() => {
        this.submitButton.textContent = originalLabel;
        this.submitButton.disabled = false;
      }, this.successDuration);
    }

    validateTextField(input) {
      const value = input.value.trim();

      if (!value) {
        input.setCustomValidity(this.validationRequired);
        return false;
      }

      if (value.length > Number(input.maxLength || 255)) {
        input.setCustomValidity(this.validationRequired);
        return false;
      }

      if (UNSAFE_TEXT_PATTERN.test(value)) {
        input.setCustomValidity(this.validationInvalidCharacters);
        return false;
      }

      input.setCustomValidity('');
      return true;
    }

    validate() {
      this.clearStatus();

      if (this.honeypot?.value) return false;

      this.form.querySelectorAll('.form-input').forEach((input) => input.setCustomValidity(''));

      const firstValid = this.validateTextField(this.firstNameInput);
      const lastValid = this.validateTextField(this.lastNameInput);
      const messageValid = this.validateTextField(this.messageInput);

      const email = this.emailInput?.value.trim() || '';
      if (!email || !EMAIL_PATTERN.test(email)) {
        this.emailInput?.setCustomValidity(this.validationEmail);
      } else {
        this.emailInput?.setCustomValidity('');
      }

      if (!this.getSelectedReasonSlug()) {
        this.reasonSelect?.setCustomValidity(this.validationReason);
      } else {
        this.reasonSelect?.setCustomValidity('');
      }

      if (!firstValid || !lastValid || !messageValid || !this.form.checkValidity()) {
        this.form.reportValidity();
        return false;
      }

      return true;
    }

    async onSubmit(event) {
      event.preventDefault();
      if (this._submitting) return;

      this.combineName();
      this.updateRouting();
      if (!this.validate()) return;

      this._submitting = true;
      this.setSubmittingState(true);

      try {
        const response = await fetch(this.form.action, {
          method: 'POST',
          body: new FormData(this.form),
          headers: {
            Accept: 'text/html',
          },
        });

        if (response.url.includes('contact_posted=true')) {
          this.form.reset();
          this.updateRouting();
          this.hideServerSuccessBanner();
          this.showButtonSuccess();
          this.clearStatus();
          this._submitting = false;
          return;
        }

        this.showStatus(this.errorLabel, true);
      } catch (error) {
        this.showStatus(this.errorLabel, true);
      } finally {
        this._submitting = false;
        if (this.submitButton?.textContent !== this.successLabel) {
          this.setSubmittingState(false);
        }
      }
    }
  }

  customElements.define('bhcr-contact-form', BhcrContactForm);
}
